// Shared Options Scanner pieces (BUY and SELL use the same layout): the filter bar, the Scanner Settings
// button, the Columns menu and the table itself. Display only - every number still comes from the worker.
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useHeldRows, heldNote } from "@/lib/heldRows";
import { Columns3, GripVertical, RotateCcw, Settings2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { defaultPrefs, loadPrefs, moveColumn, prefsKey, savePrefs, toggleColumn, visibleColumns } from "@/lib/columnPrefs";

export const CONTROL = "h-8 bg-[var(--term-input)] border border-[var(--term-border-strong)] rounded-sm px-2 text-xs";

/** Every filter: a label above a control of one height, so BUY and SELL rows line up the same way. */
export function FilterField({ label, title, children, testId }) {
  return (
    <label className="flex flex-col gap-1 min-w-[8rem]" title={title} data-testid={testId}>
      <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--term-muted)]">{label}</span>
      {children}
    </label>
  );
}

/** All filters visible, in aligned responsive rows; actions (Columns, Scanner Settings) on the right. */
export function FilterBar({ children, actions, testId }) {
  return (
    <div className="term-panel rounded-sm p-2 flex flex-wrap items-end gap-x-3 gap-y-2 text-xs" data-testid={testId}>
      {children}
      <div className="ml-auto flex items-end gap-2">{actions}</div>
    </div>
  );
}

export function ScannerSettingsButton() {
  return (
    <Link to="/options/settings" data-testid="options-scanner-settings-button"
      className="h-8 inline-flex items-center gap-1.5 px-3 rounded-sm border border-[var(--term-border-strong)] bg-[var(--term-s2)] font-semibold text-xs hover:border-[#2563EB] hover:text-[#2563EB]">
      <Settings2 className="h-3.5 w-3.5" /> Scanner Settings
    </Link>
  );
}

/** Column preferences for one table (BUY / SELL), saved per user in this browser. */
export function useColumnPrefs(table, optional) {
  const { user } = useAuth() || {};
  const key = prefsKey(user?.id, table);
  const [prefs, setPrefsState] = useState(() => loadPrefs(key, optional));
  useEffect(() => { setPrefsState(loadPrefs(key, optional)); }, [key]);   // eslint-disable-line react-hooks/exhaustive-deps
  const setPrefs = useCallback((p) => { setPrefsState(p); savePrefs(key, p); }, [key]);
  const reset = useCallback(() => setPrefs(defaultPrefs(optional)), [setPrefs, optional]);
  return { prefs, setPrefs, reset };
}

/** Columns button: show / hide and reorder (drag, or the arrows) the optional columns; Restore defaults. */
export function ColumnsMenu({ protectedCols, optional, prefs, setPrefs, reset, testId = "options-columns" }) {
  const [open, setOpen] = useState(false);
  const [drag, setDrag] = useState(null);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  const byId = Object.fromEntries(optional.map((c) => [c.id, c]));
  const shown = optional.length - prefs.hidden.length;
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen(!open)} data-testid={`${testId}-button`} aria-expanded={open}
        className="h-8 inline-flex items-center gap-1.5 px-3 rounded-sm border border-[var(--term-border-strong)] bg-[var(--term-s2)] font-semibold text-xs hover:border-[#2563EB] hover:text-[#2563EB]">
        <Columns3 className="h-3.5 w-3.5" /> Columns <span className="text-[var(--term-muted)] font-normal">{shown}/{optional.length}</span>
      </button>
      {open && (
        <div className="absolute right-0 mt-1 z-30 w-72 term-panel rounded-sm shadow-lg p-2 text-xs bg-[var(--term-s1)]" data-testid={`${testId}-menu`}>
          <div className="text-[10px] font-semibold uppercase tracking-wide text-[var(--term-muted)] mb-1">Always shown</div>
          {protectedCols.map((c) => (
            <div key={c.id} className="flex items-center gap-2 px-1 py-0.5 text-[var(--term-muted)]" data-testid={`${testId}-fixed-${c.id}`}>
              <input type="checkbox" checked disabled /> {c.label || c.id}</div>))}
          <div className="text-[10px] font-semibold uppercase tracking-wide text-[var(--term-muted)] mt-2 mb-1">Optional — drag to reorder</div>
          <ul className="max-h-72 overflow-auto">
            {prefs.order.map((id, i) => byId[id] && (
              <li key={id} draggable data-testid={`${testId}-item-${id}`}
                onDragStart={() => setDrag(id)} onDragOver={(e) => e.preventDefault()}
                onDrop={() => { if (drag && drag !== id) setPrefs(moveColumn(prefs, drag, i)); setDrag(null); }}
                className={`flex items-center gap-2 px-1 py-0.5 rounded-sm hover:bg-[var(--term-hover)] ${drag === id ? "opacity-50" : ""}`}>
                <GripVertical className="h-3 w-3 cursor-grab text-[var(--term-muted)]" />
                <input type="checkbox" checked={!prefs.hidden.includes(id)} onChange={() => setPrefs(toggleColumn(prefs, id))}
                  data-testid={`${testId}-toggle-${id}`} />
                <span className="flex-1">{byId[id].label}</span>
                <button type="button" className="px-1 text-[var(--term-muted)] disabled:opacity-30" disabled={i === 0}
                  onClick={() => setPrefs(moveColumn(prefs, id, i - 1))} aria-label={`Move ${byId[id].label} up`} data-testid={`${testId}-up-${id}`}>▲</button>
                <button type="button" className="px-1 text-[var(--term-muted)] disabled:opacity-30" disabled={i === prefs.order.length - 1}
                  onClick={() => setPrefs(moveColumn(prefs, id, i + 1))} aria-label={`Move ${byId[id].label} down`} data-testid={`${testId}-down-${id}`}>▼</button>
              </li>))}
          </ul>
          <button type="button" onClick={reset} data-testid={`${testId}-reset`}
            className="mt-2 w-full h-7 inline-flex items-center justify-center gap-1 rounded-sm border border-[var(--term-border-strong)] hover:border-[#2563EB]">
            <RotateCcw className="h-3 w-3" /> Restore default columns</button>
        </div>
      )}
    </div>
  );
}

const Row = memo(function Row({ r, cols, ctx, rowProps }) {
  const p = rowProps(r);
  return (
    <tr {...p}>
      {cols.map((c) => <td key={c.id} className={typeof c.td === "function" ? c.td(r, ctx) : c.td}>{c.cell(r, ctx)}</td>)}
    </tr>
  );
});

/** The scanner table. `cols` = the columns to show (protected first); header click sorts when a column has `sort`. */
export function ScannerTable({ cols, rows, rowKey, rowProps, ctx, sort, onSort, empty, extraHead = null, extraCells = null,
  maxHeight, testId }) {
  const all = useMemo(() => (extraCells ? [...cols, ...extraCells] : cols), [cols, extraCells]);
  const h = useHeldRows(rows);
  rows = h.rows;
  return (
    <div className="term-panel rounded-sm overflow-auto relative" style={{ maxHeight }} {...h.bind}>
      {h.held && <div className="sticky top-0 left-0 z-20 text-[10px] px-2 py-0.5 bg-[#b38f00]/20 text-[#b38f00]"
        data-testid={`${testId}-held`}>{heldNote(h)}</div>}
      <table className="w-full text-xs border-collapse" data-testid={testId}>
        <thead className="sticky top-0 z-10 bg-[var(--term-s3)] text-[var(--term-secondary)]">
          <tr>
            {cols.map((c) => (
              <th key={c.id} onClick={() => c.sort && onSort && onSort(c.sort)} data-testid={c.thId || `${testId}-col-${c.id}`}
                className={`px-1.5 py-2 whitespace-nowrap font-semibold ${c.align === "left" ? "text-left" : c.align === "center" ? "text-center" : "text-right"} ${c.sort && onSort ? "cursor-pointer select-none" : ""} ${c.th ? c.th(ctx) : ""}`}>
                {c.head ? c.head(ctx) : c.label}{sort && c.sort && sort.key === c.sort ? (sort.dir === "desc" ? " ↓" : " ↑") : ""}</th>))}
            {extraHead}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={all.length + (extraHead ? 30 : 0)} className="px-4 py-10 text-center text-[var(--term-muted)]">{empty}</td></tr>}
          {rows.map((r) => <Row key={rowKey(r)} r={r} cols={all} ctx={ctx} rowProps={rowProps} />)}
        </tbody>
      </table>
    </div>
  );
}
